import {
	TRANSPORT_REMARKS_MAX,
	VEHICLE_MODEL_MAX,
	VEHICLE_PLATE_MAX,
	VEHICLE_SEATS,
	VEHICLE_STATUSES,
	VEHICLE_TYPES,
	type Vehicle,
	type VehicleType,
	makeCreateVehicleRequest,
	makePatchVehicleRequest,
} from "@seagames/contracts";
import { Field, Modal, ModalActions, Select, useUiStrings } from "@seagames/ui";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "../../lib/api/client";
import { createVehicle, invalidateFleet, updateVehicle } from "../../lib/api/transport-fleet";
import { toast } from "../../lib/ui/toast";
import { inputClass, refsOf, routeSaveError, useProblemText } from "./form-bits";
import { ClusterSelect, useProviderSelectOptions } from "./hooks";
import { useTransportFleetFormMessages as useFleetFormMessages } from "../../lib/i18n/use-form-schemas";
import { type Problems, type VehicleDraft, type VehicleField, checkVehicle, emptyVehicleDraft, hasProblems, issuesToProblems, normalizePlateInput, vehicleBody, vehicleDraftFrom, vehiclePatch } from "./model";
import { VehicleTypeIcon, useVehicleTypeLabel } from "./parts";

export type VehicleFormTarget = { mode: "create" } | { mode: "edit"; vehicle: Vehicle };

const KNOWN: readonly VehicleField[] = ["vehicle_type", "plate", "model", "cluster_id", "provider_id", "seats", "status", "has_ramp", "remarks"];

/**
 * Add or edit a vehicle (her `vhSheet`, l3.js:762): type, plate, model,
 * cluster, seats (in the type's range), status, ramp, remarks — plus the
 * company that runs it (ours: provider logins, PLAN Q1). No delete: a
 * vehicle with trips is RETIRED instead (allocations RESTRICT the row).
 * Maintenance, or a new cluster, while trips are booked → 409 with their
 * refs, shown on the field.
 */
export function VehicleFormModal({ target, onClose }: { target: VehicleFormTarget | null; onClose: () => void }) {
	if (!target) return null;
	return target.mode === "create" ? (
		<VehicleForm onClose={onClose} />
	) : (
		<VehicleForm key={target.vehicle.vehicle_id} before={target.vehicle} onClose={onClose} />
	);
}

function VehicleForm({ before, onClose }: { before?: Vehicle; onClose: () => void }) {
	const { t } = useTranslation("transportFleet");
	const { t: tc } = useTranslation("common");
	const { formatNumber } = useUiStrings();
	const typeLabel = useVehicleTypeLabel();
	const problemText = useProblemText();
	const providers = useProviderSelectOptions();
	const messages = useFleetFormMessages();
	const [draft, setDraft] = useState<VehicleDraft>(() => (before ? vehicleDraftFrom(before) : emptyVehicleDraft()));
	const [problems, setProblems] = useState<Problems<VehicleField>>({});
	const [busy, setBusy] = useState(false);
	const firstField = useRef<HTMLInputElement>(null);

	const set = <K extends VehicleField>(k: K, v: VehicleDraft[K]) => {
		setDraft((d) => ({ ...d, [k]: v }));
		setProblems((p) => (p[k] ? { ...p, [k]: undefined } : p));
	};
	const range = VEHICLE_SEATS[draft.vehicle_type];
	const upcoming = before?.upcoming_trips ?? 0;

	// A 409 that names the upcoming trips goes on the field that caused it.
	const special = (err: ApiError): Problems<VehicleField> | null => {
		if (err.code !== "CONFLICT") return null;
		const field = err.details?.["field"];
		if (field === "plate") return { plate: { key: "plateTaken" } };
		const refs = refsOf(err);
		if (refs.length > 0) {
			const on: VehicleField = field === "cluster_id" ? "cluster_id" : "status";
			return { [on]: { key: "upcomingTrips", values: { count: refs.length, refs: refs.slice(0, 3).join(", ") } } };
		}
		return null;
	};

	const submit = async (e: React.FormEvent) => {
		e.preventDefault();
		const found = checkVehicle(draft);
		setProblems(found);
		if (hasProblems(found)) return;
		setBusy(true);
		try {
			if (!before) {
				const { status, ...rest } = vehicleBody(draft);
				// RETIRED is reached by an edit only (the create schema allows ACTIVE / MAINTENANCE).
				const parsed = makeCreateVehicleRequest(messages).safeParse({ ...rest, status: status === "MAINTENANCE" ? "MAINTENANCE" : "ACTIVE" });
				if (!parsed.success) {
					setProblems(issuesToProblems(parsed.error.issues, KNOWN, "plate"));
					return;
				}
				const created = await createVehicle(parsed.data);
				toast.success(t("vehicleForm.toast.added", { plate: created.plate }));
			} else {
				const body = vehiclePatch(draft, before);
				if (Object.keys(body).length === 0) {
					onClose();
					return;
				}
				// The audit row's headline — audit_logs stays English.
				const changed = Object.keys(body).map((k) => k.replace(/_id$/, "").replace(/_/g, " "));
				const parsed = makePatchVehicleRequest(messages).safeParse({ ...body, version: before.version, reason: `Changed ${changed.join(", ")} for vehicle ${before.plate}` });
				if (!parsed.success) {
					setProblems(issuesToProblems(parsed.error.issues, KNOWN, "plate"));
					return;
				}
				await updateVehicle(before.vehicle_id, parsed.data);
				toast.success(t("vehicleForm.toast.updated", { plate: normalizePlateInput(draft.plate) }));
			}
			await invalidateFleet();
			onClose();
		} catch (err) {
			routeSaveError(err, KNOWN, setProblems, before ? t("vehicleForm.toast.updateFailed") : t("vehicleForm.toast.addFailed"), special);
		} finally {
			setBusy(false);
		}
	};

	const idp = before ? "vh-edit" : "vh-add";
	const formId = `${idp}-form`;

	return (
		<Modal
			open
			onClose={onClose}
			size="md"
			title={before ? t("vehicleForm.editTitle") : t("vehicleForm.addTitle")}
			description={before ? before.plate : t("vehicleForm.addDescription")}
			dismissible={!busy}
			initialFocusRef={firstField}
			footer={
				<ModalActions>
					<button type="button" className="btn btn-line btn-sm" onClick={onClose} disabled={busy}>
						{tc("action.cancel")}
					</button>
					<button type="submit" form={formId} className="btn btn-primary btn-sm" disabled={busy}>
						{busy && <span className="loading loading-spinner loading-xs" />}
						{t("vehicleForm.save")}
					</button>
				</ModalActions>
			}
		>
			<form id={formId} onSubmit={(e) => void submit(e)} noValidate className="grid gap-[0.75rem] sm:grid-cols-2">
				<Field label={t("vehicleForm.type")} htmlFor={`${idp}-type`} required error={problemText(problems.vehicle_type)} className="sm:col-span-2">
					<div id={`${idp}-type`} role="radiogroup" aria-label={t("vehicleForm.type")} className="grid grid-cols-3 gap-[0.25rem] rounded-field bg-gms-soft-2 p-[0.25rem]">
						{VEHICLE_TYPES.map((v: VehicleType) => {
							const on = draft.vehicle_type === v;
							return (
								<button
									key={v}
									type="button"
									role="radio"
									aria-checked={on}
									onClick={() => set("vehicle_type", v)}
									className={`inline-flex min-h-[2rem] items-center justify-center gap-[0.375rem] rounded-[0.375rem] px-[0.5rem] text-[0.8125rem] transition-colors focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-gms-navy ${
										on ? "bg-base-100 font-semibold text-gms-navy shadow-[0_1px_2px_rgba(18,20,63,0.12),0_0_0_1px_var(--color-gms-line)]" : "font-medium text-gms-ink-2 hover:text-gms-navy-ink"
									}`}
								>
									<VehicleTypeIcon type={v} className="size-[0.9375rem]" />
									{typeLabel(v)}
								</button>
							);
						})}
					</div>
				</Field>

				<Field label={t("vehicleForm.plate")} htmlFor={`${idp}-plate`} required error={problemText(problems.plate)}>
					<input
						ref={firstField}
						id={`${idp}-plate`}
						value={draft.plate}
						onChange={(e) => set("plate", e.target.value.toUpperCase())}
						autoComplete="off"
						spellCheck={false}
						maxLength={VEHICLE_PLATE_MAX}
						placeholder={t("vehicleForm.platePlaceholder")}
						className={inputClass(!!problems.plate, "uppercase tracking-[0.04em]")}
					/>
				</Field>
				<Field label={t("vehicleForm.model")} htmlFor={`${idp}-model`} required error={problemText(problems.model)}>
					<input
						id={`${idp}-model`}
						value={draft.model}
						onChange={(e) => set("model", e.target.value)}
						autoComplete="off"
						maxLength={VEHICLE_MODEL_MAX}
						placeholder={t("vehicleForm.modelPlaceholder")}
						className={inputClass(!!problems.model)}
					/>
				</Field>

				<Field label={t("vehicleForm.cluster")} htmlFor={`${idp}-cluster`} required error={problemText(problems.cluster_id)} hint={t("vehicleForm.clusterHint")}>
					<ClusterSelect id={`${idp}-cluster`} value={draft.cluster_id} onChange={(v) => set("cluster_id", v)} current={before?.cluster} />
				</Field>
				<Field
					label={t("vehicleForm.seats")}
					htmlFor={`${idp}-seats`}
					required
					error={problemText(problems.seats)}
					hint={t("vehicleForm.seatsHint", { type: typeLabel(draft.vehicle_type), min: formatNumber(range.min), max: formatNumber(range.max) })}
				>
					<input
						id={`${idp}-seats`}
						type="number"
						inputMode="numeric"
						min={range.min}
						max={range.max}
						step={1}
						value={draft.seats}
						onChange={(e) => set("seats", e.target.value)}
						className={inputClass(!!problems.seats, "tabular-nums")}
					/>
				</Field>

				<Field
					label={t("vehicleForm.status")}
					htmlFor={`${idp}-status`}
					required
					error={problemText(problems.status)}
					hint={upcoming > 0 ? t("vehicleForm.upcoming", { count: upcoming, n: formatNumber(upcoming) }) : t("vehicleForm.noUpcoming")}
				>
					<Select
						id={`${idp}-status`}
						value={draft.status}
						onChange={(v) => v && set("status", v as VehicleDraft["status"])}
						options={VEHICLE_STATUSES.filter((s) => before || s !== "RETIRED").map((s) => ({ value: s, label: t(`vehicleStatus.${s}`), description: t(`vehicleStatusHint.${s}`) }))}
						searchable={false}
					/>
				</Field>
				<Field label={t("vehicleForm.company")} htmlFor={`${idp}-company`} aside={tc("state.optional")} error={problemText(problems.provider_id)} hint={t("vehicleForm.companyHint")}>
					<Select
						id={`${idp}-company`}
						value={draft.provider_id || null}
						onChange={(v) => set("provider_id", v ?? "")}
						options={
							before?.provider && !providers.options.some((o) => o.value === before.provider?.transport_provider_id)
								? [{ value: before.provider.transport_provider_id, label: before.provider.name }, ...providers.options]
								: providers.options
						}
						placeholder={t("vehicleForm.companyNone")}
						clearable
					/>
				</Field>

				<label className="flex cursor-pointer items-center gap-[0.625rem] text-[0.84375rem] text-gms-navy-ink sm:col-span-2">
					<input type="checkbox" className="checkbox checkbox-sm checkbox-primary" checked={draft.has_ramp} onChange={(e) => set("has_ramp", e.target.checked)} />
					{t("vehicleForm.ramp")}
				</label>

				<Field label={t("vehicleForm.remarks")} htmlFor={`${idp}-remarks`} aside={tc("state.optional")} error={problemText(problems.remarks)} className="sm:col-span-2">
					<input
						id={`${idp}-remarks`}
						value={draft.remarks}
						onChange={(e) => set("remarks", e.target.value)}
						autoComplete="off"
						maxLength={TRANSPORT_REMARKS_MAX}
						placeholder={t("vehicleForm.remarksPlaceholder")}
						className={inputClass(!!problems.remarks)}
					/>
				</Field>
			</form>
		</Modal>
	);
}
